import React from 'react';

import { ChartCard } from '@/design-system/chart-card';
import type {
  StageTimelineResponse,
  StageTimelineStage,
} from '@/services/api/dashboard';

import { fmtDuration, fmtInt } from './format';

const STAGE_META: Record<
  string,
  { label: string; kind: 'work' | 'gap' }
> = {
  inbound: { label: 'Inbound scan', kind: 'work' },
  gap1: { label: 'Idle', kind: 'gap' },
  dispatch: { label: 'Dispatch', kind: 'work' },
  gap2: { label: 'Rider wait', kind: 'gap' },
  riderScan: { label: 'Rider scan', kind: 'work' },
};

/** Ink ramp for work stages, tint for idle gaps — color carries no data here. */
const WORK_FILL = ['bg-gray-900', 'bg-gray-600', 'bg-gray-400'];

/**
 * Median dispatch-cycle waterfall: inbound → idle → dispatch → rider wait →
 * rider scan, each segment sized by its median duration. The dispatch stage
 * measures from the effective start (max of preview creation and the last
 * inbound scan), so cycles where the preview overlapped inbound don't bill
 * the inbound window to dispatch; those cycles are counted below.
 */
const StageWaterfall: React.FC<{
  timeline: StageTimelineResponse | null;
  loading: boolean;
}> = ({ timeline, loading }) => {
  const stages = timeline?.stages ?? [];
  const totalSec = stages.reduce(
    (sum, s) => sum + (s.medianDurationSec ?? 0),
    0
  );
  const state = loading
    ? 'loading'
    : !timeline || timeline.status !== 'OK' || totalSec === 0
      ? 'empty'
      : 'ready';

  let workIdx = -1;
  const segments = stages
    .filter((s) => (s.medianDurationSec ?? 0) > 0)
    .map((s: StageTimelineStage) => {
      const meta = STAGE_META[s.key] ?? { label: s.key, kind: 'work' as const };
      if (meta.kind === 'work') workIdx += 1;
      return {
        key: s.key,
        label: meta.label,
        kind: meta.kind,
        sec: s.medianDurationSec ?? 0,
        items: s.medianItemCount,
        fill: meta.kind === 'gap' ? 'bg-gray-200' : WORK_FILL[workIdx % 3],
      };
    });

  return (
    <ChartCard
      eyebrow="Per dispatch cycle · medians"
      title="Stage waterfall"
      subtitle={
        timeline
          ? `${fmtInt(timeline.cycles.count)} cycles · total ${fmtDuration(totalSec)}`
          : undefined
      }
      state={state}
      emptyMessage="No committed dispatch cycles in this window"
      bodyClassName="px-4 pb-4"
      footer={
        timeline && timeline.cycles.overlappedCycles > 0 ? (
          <span className="text-[11px] text-text-sub-600">
            <i className="ri-information-line mr-1" />
            {timeline.cycles.overlappedCycles} of {timeline.cycles.count}{' '}
            cycles previewed before inbound finished — dispatch is measured
            from the last inbound scan for those.
          </span>
        ) : undefined
      }
    >
      <div className="pt-2">
        <div className="flex h-7 w-full overflow-hidden rounded-sm">
          {segments.map((seg) => (
            <div
              key={seg.key}
              className={`${seg.fill} h-full`}
              style={{
                width: `${Math.max((seg.sec / totalSec) * 100, 1.5)}%`,
              }}
              title={`${seg.label} · ${fmtDuration(seg.sec)}`}
            />
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3 lg:grid-cols-5">
          {segments.map((seg) => (
            <div key={seg.key} className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span
                  className={`h-2 w-2 shrink-0 rounded-full ${seg.fill}`}
                />
                <span className="truncate text-[11px] font-semibold tracking-tight text-text-strong-950">
                  {seg.label}
                </span>
              </div>
              <p className="mt-0.5 pl-3.5 font-mono text-[11px] tabular-nums text-text-sub-600">
                {fmtDuration(seg.sec)}
                {seg.items != null && ` · ${fmtInt(seg.items)} pkgs`}
              </p>
            </div>
          ))}
        </div>
      </div>
    </ChartCard>
  );
};

export default StageWaterfall;
